import React, { useEffect, useState } from "react";
import { Button, Card, Col, Modal, Result, Row, Skeleton } from "antd";
import { Company } from "interfaces/companies";
import axios from "axios";
import { useHistory, useParams } from "react-router-dom";
import SanitizeHTML from "components/SanitizeHTML/SanitizeHTML";
import CompanyHeaderBox from "components/CompanyHeaderBox/CompanyHeaderBox";
import { DeleteOutlined, EditOutlined, LockOutlined } from "@ant-design/icons";
import { message } from "components/Messages/Messages";
import AdminCompanyContacts from "./Sections/AdminCompanyContacts";
import AllCompanyEvents from "./Sections/AllCompanyEvents";
import AllCompanyJobs from "./Sections/AllCompanyJobs";
import DetailedInformation from "./Sections/DetailedInformation";
import EditCompanyModal from "./Sections/EditCompanyModal";
import ApproveCompany from "./Sections/ApproveCompany";
import CompanyComments from "./Sections/CompanyComments";
import ResetPasswordModal from "./Sections/ResetPasswordModal";

const AdminSingleCompany = () => {
  const [company, setCompany] = useState<Company>();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [editCompanyModalIsOpen, setEditCompanyModalIsOpen] = useState<boolean>(false);
  const [resetPasswordModalIsOpen, setResetPasswordModalIsOpen] = useState<boolean>(false);
  const [loadingDelete, setLoadingDelete] = useState<boolean>(false);
  const [fetchingCompany, setFetchingCompany] = useState<boolean>(true);

  useEffect(() => {
    axios
      .get<Company>(`/companies/${id}/`)
      .then(response => setCompany(response.data))
      .finally(() => setFetchingCompany(false));
  }, [id]);

  const deleteCompany = () => {
    Modal.confirm({
      title: `Er du sikker på at du vil slette ${company?.name ?? "bedriften"}?`,
      content: "Denne handlingen kan ikke angres",
      okText: "Slett",
      okType: "danger",
      cancelText: "Avbryt",
      type: "error",
      onOk() {
        setLoadingDelete(true);
        axios
          .delete(`/companies/${id}/`)
          .then(() => {
            message("success", "Bedriften ble slettet");
            history.push("/admin/companies/");
            setLoadingDelete(false);
          })
          .catch(() => {
            message("error", "Feil ved sletting av bedrift");
            setLoadingDelete(false);
          });
      },
    });
  };

  if (fetchingCompany)
    return (
      <>
        <Row>
          <Skeleton.Button block active style={{ height: "125px" }} />
        </Row>
        <Row style={{ marginTop: "16px" }} gutter={[16, 16]}>
          <Col md={8} sm={24} xs={24}>
            <Skeleton.Button block active style={{ height: "350px" }} />
            <Skeleton.Button block active style={{ height: "150px", margin: "16px 0" }} />
            <Skeleton.Button block active style={{ height: "150px" }} />
          </Col>
          <Col md={16} sm={24} xs={24}>
            <Skeleton.Button block active style={{ height: "75px" }} />
            <Skeleton.Button block active style={{ height: "250px", margin: "16px 0" }} />
            <Skeleton.Button block active style={{ height: "250px" }} />
          </Col>
        </Row>
        <Row>
          <Skeleton.Button block active style={{ height: "75px", margin: "16px 0" }} />
        </Row>
      </>
    );

  if (!company)
    return <Result title="Fant ikke bedriften" subTitle="Dobbeltsjekk URL eller prøv på nytt" status="404" />;
  return (
    <>
      <EditCompanyModal
        company={company}
        open={editCompanyModalIsOpen}
        onClose={() => setEditCompanyModalIsOpen(false)}
        onSave={c => setCompany(c)}
      />
      {resetPasswordModalIsOpen && (
        <ResetPasswordModal company={company} onClose={() => setResetPasswordModalIsOpen(false)} />
      )}
      {!company.approved && <ApproveCompany company={company} handleApproveCompany={c => setCompany(c)} />}
      <CompanyHeaderBox company={company} />
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col md={8} sm={24}>
          <Row>
            <DetailedInformation company={company} />
          </Row>
          <Row>
            <AdminCompanyContacts companyId={company.id} />
          </Row>
          <Row>
            <Card title="Beskrivelse" style={{ width: "100%", margin: "16px auto" }}>
              <SanitizeHTML html={company.description} />
            </Card>
          </Row>
        </Col>
        <Col md={16} sm={24}>
          <Row>
            <Card style={{ width: "100%" }}>
              <Row gutter={10} justify="space-between">
                <Col span={4.8}>
                  <Button
                    style={{ width: "100%" }}
                    type="primary"
                    onClick={() => history.push(`/admin/events/new/${company.id}/`)}
                  >
                    Nytt arrangement
                  </Button>
                </Col>
                <Col span={4.8}>
                  <Button
                    style={{ width: "100%" }}
                    type="primary"
                    onClick={() => history.push(`/admin/companies/${company.id}/create-new-job/`)}
                  >
                    Ny stillingsannonse
                  </Button>
                </Col>
                <Col span={4.8}>
                  <Button
                    style={{ width: "100%" }}
                    onClick={() => setResetPasswordModalIsOpen(true)}
                    icon={<LockOutlined />}
                  >
                    Tilbakestill passord
                  </Button>
                </Col>
                <Col span={4.8}>
                  <Button
                    style={{ width: "100%" }}
                    icon={<EditOutlined />}
                    onClick={() => setEditCompanyModalIsOpen(true)}
                  >
                    Rediger
                  </Button>
                </Col>
                <Col span={4.8}>
                  <Button
                    style={{ width: "100%" }}
                    type="primary"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={deleteCompany}
                    loading={loadingDelete}
                  >
                    Slett
                  </Button>
                </Col>
              </Row>
            </Card>
          </Row>
          <Row style={{ margin: "16px auto" }}>
            <AllCompanyEvents companyId={company.id} />
          </Row>
          <Row>
            <AllCompanyJobs companyId={company.id} />
          </Row>
          <Row style={{ margin: "16px auto" }}>
            <CompanyComments companyId={company.id} />
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default AdminSingleCompany;
